import { ReviewItem, ReviewTemplate } from '../types';
import { safeNegativeId } from '../utils/idUtils';

export interface OfflineOnDemandItemRecord {
  id?: number | string | null;
  temp_id?: string | null;
  category_id?: number | string | null;
  name: string;
  description?: string;
  questions_schema?: any;
}

export type ReviewItemWithTempId = ReviewItem & { tempId?: string | null };

const normalizeText = (value: unknown): string => (value ?? '').toString().trim().toLowerCase();

const getOnDemandSignature = (item: {
  categoryId?: number | string | null;
  category_id?: number | string | null;
  name?: string | null;
  description?: string | null;
}): string => {
  const categoryId = item.categoryId ?? item.category_id ?? '';
  return [
    Number(categoryId) || '',
    normalizeText(item.name),
    normalizeText(item.description),
  ].join('|');
};

export function resolveOfflineOnDemandItemId(
  item: OfflineOnDemandItemRecord,
  fallbackTimestamp: () => number = Date.now
): number {
  if (item.temp_id?.startsWith('synced_')) {
    const serverId = Number.parseInt(item.temp_id.replace('synced_', ''), 10);
    if (!Number.isNaN(serverId)) {
      return serverId;
    }
  }

  if (item.temp_id) {
    return safeNegativeId(item.temp_id);
  }

  const numericId = Number(item.id);
  if (Number.isFinite(numericId) && numericId !== 0) {
    return -Math.abs(numericId);
  }

  const tempIdNumber = Number.parseInt((item.temp_id || '').replace(/\D/g, ''), 10);
  return -Math.abs(Number.isFinite(tempIdNumber) ? tempIdNumber : fallbackTimestamp());
}

export function mapOfflineOnDemandItemToReviewItem(
  item: OfflineOnDemandItemRecord,
  reviewId: number
): ReviewItemWithTempId {
  return {
    id: resolveOfflineOnDemandItemId(item),
    categoryId: Number(item.category_id) || 0,
    name: item.name,
    description: item.description,
    questionsSchema: item.questions_schema || {},
    displayOrder: 999,
    isRequired: false,
    defaultPresent: false,
    isOnDemand: true,
    homeReviewId: reviewId,
    tempId: item.temp_id,
  };
}

export function addOfflineOnDemandItemsToTemplate(
  template: ReviewTemplate,
  offlineItems: OfflineOnDemandItemRecord[],
  reviewId: number
): ReviewItemWithTempId[] {
  if (!Array.isArray(template.categories) || offlineItems.length === 0) {
    return [];
  }

  const existingOnDemandIds = new Set<number>();
  const existingOnDemandSignatures = new Set<string>();
  template.categories.forEach(category => {
    category.items?.forEach(item => {
      if (item.isOnDemand && typeof item.id === 'number') {
        existingOnDemandIds.add(item.id);
        existingOnDemandSignatures.add(getOnDemandSignature(item));
      }
    });
  });

  const addedItems: ReviewItemWithTempId[] = [];

  offlineItems.forEach(item => {
    const reviewItem = mapOfflineOnDemandItemToReviewItem(item, reviewId);

    if (existingOnDemandIds.has(reviewItem.id)) {
      return;
    }
    if (existingOnDemandSignatures.has(getOnDemandSignature(reviewItem))) {
      return;
    }

    const category = template.categories!.find(
      category => Number(category.id) === reviewItem.categoryId
    );
    if (!category) {
      return;
    }

    category.items = category.items || [];
    category.items.push(reviewItem);
    existingOnDemandIds.add(reviewItem.id);
    existingOnDemandSignatures.add(getOnDemandSignature(reviewItem));
    addedItems.push(reviewItem);
  });

  return addedItems;
}

export function collectOnDemandItemsFromTemplate(
  template?: ReviewTemplate | null
): ReviewItemWithTempId[] {
  if (!Array.isArray(template?.categories)) {
    return [];
  }

  const items: ReviewItemWithTempId[] = [];
  template.categories.forEach(category => {
    category.items?.forEach(item => {
      if (item.isOnDemand) {
        items.push(item as ReviewItemWithTempId);
      }
    });
  });

  return items;
}
